import { useEffect, useState, useRef, useCallback } from "react";
import { Skeleton } from "antd";
import toast from "react-hot-toast";
import { useIsMutating } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

import { useProductStore } from "@/_zustand/store";
import { useMachine } from "@/_zustand/machine";
import { useFormTransaction } from "@/hooks/useTransbank";
import { useTransbankCredenciales } from "@/hooks/useTransbankCredenciales";
import { getEnvVariables } from "@/constants/getEnvVariables";
import {
  generarBuyOrderWebpay,
  debeReportarBloqueo,
  PAYMENT_MSG_EN_CURSO,
} from "@/lib/paymentGuard";
import {
  credencialesCompletas,
  filtrarCredencialesParaBody,
  guardarCredencialesIntento,
} from "@/lib/transbankCredenciales";

export const WebPayButton = () => {
  const { DOMAIN } = getEnvVariables();
  const { total, discount, setBuyOrder } = useProductStore();
  const totalFinal = Math.max(0, total - discount);
  const { machineSelected, addressMachine } = useMachine();
  const { mutateAsync } = useFormTransaction();
  const credenciales = useTransbankCredenciales("webpay");
  const isMutating = useIsMutating({ mutationKey: ["webpayformRequest"] });
  const [formData, setFormData] = useState({ url: "", token: "" });
  const [counter, setCounter] = useState(240); // 240 segundos = 4 minutos
  const [submitted, setSubmitted] = useState(false);
  const submittedRef = useRef(false);
  const isCompanyFlow = location.pathname.startsWith("/company");

  // getFormPay regenera el buyOrder por intento (AC 11/12): NO debe incluir
  // buyOrder/setBuyOrder en sus deps — hacerlo cambiaría la identidad del
  // callback en cada llamada y re-dispararía el efecto que lo invoca,
  // produciendo un bucle infinito de creación de transacciones Webpay.
  const getFormPay = useCallback(() => {
    const bo = generarBuyOrderWebpay();
    setBuyOrder(bo);

    // Borde 2: si faltan credenciales (machine vacío/nulo), no se bloquea
    // (CartPage.jsx ya oculta Webpay en ese caso) — sólo se reporta un
    // warning, sin exponer el apiKey.
    if (!credencialesCompletas(credenciales, "webpay")) {
      Sentry.captureMessage(
        "getFormTransaction sin credenciales de comercio Webpay resolubles",
        {
          level: "warning",
          tags: { payment_flow: "webpay" },
          extra: { maquina_id: machineSelected },
        },
      );
    }

    let formDataSend = {
      buy_order: bo,
      session_id: addressMachine.replace(/[^a-zA-Z0-9]/g, "-"),
      amount: Math.round(totalFinal),
      return_url: isCompanyFlow
        ? `${DOMAIN}/company/pay-detail?platform=webpay`
        : `${DOMAIN}/pay-detail?platform=webpay`,
      maquina_id: machineSelected,
      ...filtrarCredencialesParaBody({
        codeComerce: credenciales.codeComerce,
        apiKey: credenciales.apiKey,
      }),
    };

    mutateAsync(formDataSend)
      .then((resp) => {
        guardarCredencialesIntento("wp:" + resp.token, credenciales);
        setFormData({ url: resp.url, token: resp.token });
      })
      .catch(() => {
        toast.error("Error al crear transacción");
      });
  }, [
    addressMachine,
    totalFinal,
    isCompanyFlow,
    DOMAIN,
    machineSelected,
    mutateAsync,
    setBuyOrder,
    credenciales,
  ]);

  useEffect(() => {
    getFormPay();
    // Un solo efecto de montaje/cambio de total — antes existían dos efectos
    // equivalentes ([total, discount, getFormPay] y [getFormPay]) que creaban
    // dos transacciones Webpay por cada cambio.
  }, [total, discount, getFormPay]);

  useEffect(() => {
    const timer = setInterval(() => {
      // Congela el refresh de 240s tras el submit (edge 15): evita crear un
      // nuevo token/buyOrder mientras el formulario ya viaja a Transbank con
      // el token vigente.
      if (submittedRef.current) {
        clearInterval(timer);
        return;
      }
      setCounter((prevCounter) => {
        if (prevCounter <= 1) {
          clearInterval(timer);
          getFormPay();
          return 0;
        }
        return prevCounter - 1;
      });
    }, 1000);
    setCounter(240);
    return () => clearInterval(timer); // Limpiar el intervalo al desmontar el componente
  }, [formData, getFormPay]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const handleSubmit = (e) => {
    if (submittedRef.current || !formData.token) {
      e.preventDefault();
      if (submittedRef.current && debeReportarBloqueo("webpay")) {
        Sentry.captureMessage(
          "Segundo intento de pago bloqueado por candado",
          {
            level: "warning",
            tags: { payment_flow: "webpay" },
            extra: {
              buyOrder: useProductStore.getState().buyOrder,
              motivo: "submit_duplicado",
            },
          },
        );
      }
      toast.error(
        !formData.token
          ? "Estamos preparando el pago, espera un momento"
          : PAYMENT_MSG_EN_CURSO,
      );
      return;
    }
    setSubmitted(true);
    submittedRef.current = true;
  };

  return (
    <Skeleton loading={isMutating} active>
      <form method="post" action={formData.url} onSubmit={handleSubmit}>
        <input type="hidden" name="token_ws" value={formData.token} />
        {/* <Button type='submit' className="btn verde text-white inter-semibold" size="md">

        </Button>  */}
        <button
          type="submit"
          className="order-btn-fill"
          disabled={submitted || isMutating > 0 || !formData.token}
        >
          Pagar
        </button>
        <p>Tiempo restante: {formatTime(counter)}</p>
      </form>
    </Skeleton>
  );
};
